import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clapperboard, FileText, Lock, Plus, Radio, Swords, Users } from "lucide-react";
import { api } from "@/lib/api";
import PostCard from "@/components/PostCard";

const TABS = [
  { key: "posts", label: "Posts", Icon: FileText },
  { key: "reels", label: "Reels", Icon: Clapperboard },
  { key: "live", label: "Live", Icon: Radio },
  { key: "communities", label: "Communities", Icon: Users },
  { key: "vault", label: "Vault", Icon: Lock },
  { key: "leaderboard", label: "Leaderboard", Icon: Swords },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

function useTabData(handle, tab) {
  const [data, setData] = useState({});
  useEffect(() => {
    if (!handle || tab === "posts" || tab === "vault" || data[tab]) return;
    const path = tab === "reels" ? `/users/${handle}/reels` : tab === "live" ? `/users/${handle}/live` : `/users/${handle}/communities`;
    api.get(path).then(({ data: d }) => setData((s) => ({ ...s, [tab]: d }))).catch(() => setData((s) => ({ ...s, [tab]: tab === "live" ? { recent: [] } : [] })));
  }, [handle, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

/** Part 6 — unified profile: Posts · Reels · Live · Communities · Vault (permitted). */
export default function ProfileTabs({ handle, profile, posts, vault, extraVault, leaderboard, isMe, onCreatePost, onPostsChanged }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "posts";
  const setTab = (k) => setParams((p) => { p.set("tab", k); return p; }, { replace: true });
  const data = useTabData(handle, tab);
  const fromExplore = (() => { try { return Date.now() - Number(sessionStorage.getItem("explore:return") || 0) < 5 * 60 * 1000; } catch { return false; } })();

  return (
    <div data-testid="profile-tabs">
      {fromExplore && (
        <button
          type="button"
          data-testid="profile-back-to-explore"
          onClick={() => navigate(-1)}
          className="mb-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
        >
          <ArrowLeft size={12} /> Back to Social Meet
        </button>
      )}
      <div role="tablist" aria-label="Profile sections" className="flex gap-0.5 sm:gap-1 overflow-x-auto border-b border-white/10 mb-5 -mx-1 px-1 [scrollbar-width:none]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`profile-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`shrink-0 min-h-[40px] inline-flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-medium px-1.5 sm:px-3 py-2 border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-emerald-400 text-emerald-300" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
          >
            <t.Icon size={13} /> {t.label}
            {t.key === "live" && profile?.is_live ? <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> : null}
          </button>
        ))}
      </div>

      {tab === "posts" && (
        <div data-testid="profile-panel-posts" className="space-y-5">
          {isMe && (
            <button type="button" data-testid="wall-add-media-button" onClick={onCreatePost} className="w-full card-surface p-4 flex items-center gap-3 text-left hover:border-emerald-400/50 transition-colors">
              <span className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center"><Plus size={18} /></span>
              <span><span className="block text-sm text-white">Add to your wall</span><span className="block text-xs text-zinc-500">Photo, video or a few words — choose who sees it</span></span>
            </button>
          )}
          {posts.length === 0 && <div className="card-surface p-10 text-center text-sm text-zinc-500">{isMe ? "Your wall is empty — share your first photo or video." : "Nothing on the wall yet."}</div>}
          {posts.map((p) => <PostCard key={p.id} post={p} onChanged={onPostsChanged} />)}
        </div>
      )}

      {tab === "leaderboard" && (
        <div data-testid="profile-panel-leaderboard" className="space-y-4">
          <div className="text-xs text-zinc-500">Bamboo and battle rankings, history and statistics live here — off the main profile.</div>
          {leaderboard}
        </div>
      )}

      {tab === "reels" && (
        <div data-testid="profile-panel-reels" className="grid grid-cols-3 gap-2">
          {!data.reels ? <div className="col-span-3 text-center text-xs text-zinc-500 py-8 uppercase tracking-widest">Loading…</div>
            : data.reels.length === 0 ? <div className="col-span-3 card-surface p-8 text-center text-sm text-zinc-500">No reels yet.</div>
            : data.reels.map((r) => (
              <Link key={r.id} to={`/nearby?tab=all&reel=${r.id}`} data-testid={`profile-reel-${r.id}`} className="relative aspect-[9/16] bg-zinc-900 border border-white/10 overflow-hidden group">
                {r.thumbnail_url ? <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <video src={r.video_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />}
                <div className="absolute inset-x-0 bottom-0 p-1.5 text-[10px] text-white bg-gradient-to-t from-black/70 to-transparent truncate">{r.caption || `${r.views} views`}</div>
              </Link>
            ))}
        </div>
      )}

      {tab === "live" && (
        <div data-testid="profile-panel-live" className="space-y-3">
          {!data.live ? <div className="text-center text-xs text-zinc-500 py-8 uppercase tracking-widest">Loading…</div> : (
            <>
              {data.live.live_now ? (
                <Link to={`/live?s=${data.live.live_now.id}`} data-testid="profile-live-now" className="card-surface p-4 flex items-center justify-between border-red-500/40 hover:border-red-500">
                  <div className="flex items-center gap-3"><span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5 animate-pulse"><Radio size={9} /> Live</span><span className="text-sm">{data.live.live_now.title || "Live now"}</span></div>
                  <span className="text-xs text-zinc-400 inline-flex items-center gap-1"><Users size={12} /> {data.live.live_now.viewer_count ?? 0}</span>
                </Link>
              ) : <div className="text-xs text-zinc-500">Not live right now.</div>}
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 pt-2">Recent lives · {data.live.total_streams ?? 0} total</div>
              {(data.live.recent || []).length === 0 ? <div className="card-surface p-6 text-center text-sm text-zinc-500">No past lives yet.</div>
                : data.live.recent.map((s) => (
                  <div key={s.id} data-testid={`profile-live-recent-${s.id}`} className="card-surface p-3 flex items-center justify-between text-sm">
                    <span className="truncate">{s.title || "Untitled live"}</span>
                    <span className="text-xs text-zinc-500 shrink-0 ml-3">{fmtDate(s.started_at)}{s.peak_viewers != null ? ` · peak ${s.peak_viewers}` : ""}</span>
                  </div>
                ))}
            </>
          )}
        </div>
      )}

      {tab === "communities" && (
        <div data-testid="profile-panel-communities" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!data.communities ? <div className="text-center text-xs text-zinc-500 py-8 uppercase tracking-widest">Loading…</div>
            : data.communities.length === 0 ? <div className="sm:col-span-2 card-surface p-8 text-center text-sm text-zinc-500">No public communities.</div>
            : data.communities.map((c) => (
              <Link key={c.id} to={`/c/${c.id}`} data-testid={`profile-community-${c.id}`} className="card-surface p-4 flex items-center justify-between hover:border-rose-500/50">
                <span className="text-sm truncate">{c.name}</span>
                <span className="text-xs text-zinc-500 inline-flex items-center gap-1 shrink-0 ml-3"><Users size={12} /> {c.members_count ?? 0}</span>
              </Link>
            ))}
        </div>
      )}

      {tab === "vault" && (
        <div data-testid="profile-panel-vault">
          <div className="text-xs text-zinc-500 mb-3">Only content this member has made visible to you appears here.</div>
          {vault}
          {extraVault}
        </div>
      )}
    </div>
  );
}
